import { IconName } from '@fortawesome/fontawesome-svg-core';
import { EventInfo, EventViewData } from 'components/eventView/pages/eventData';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
    GuideQuery_guide,
    GuideQuery_guide_entityTypes
} from 'graphql/query/__generated__/GuideQuery';
import { GuideQueryView_guideView } from 'graphql/query/__generated__/GuideQueryView';
import { GUIDE_THEMES } from 'static/config/guideThemes';
import { Localization } from 'static/localization/localization';

dayjs.extend(utc);

function getLocalization(name: string) {
    if (name?.[0] === '_') {
        const upper = name?.substring(1)?.toUpperCase();
        if (Localization[upper]) {
            return Localization[upper];
        }
    }
    return name;
}

function mapOtherEntityToEventView(
    guide: GuideQuery_guide | GuideQueryView_guideView,
    entityType: GuideQuery_guide_entityTypes
): EventInfo['otherEntities'][0] {
    const stories = guide.stories.filter((story) => entityType.storyIds.includes(story.storyId));
    const storiesCategoryIds = stories.flatMap((story) =>
        story.categories.map((e) => e.worldMapSetId)
    );
    const categoryIds = guide.categories
        .map((e) => e.worldMapSetId)
        .filter((e) => storiesCategoryIds.includes(e));

    return {
        id: entityType.guideEntityTypeId,
        name: getLocalization(entityType.name),
        description: entityType.description,
        icon: entityType.icon as IconName,
        listIds: entityType.storyIds.map((storyId) => storyId),
        link:
            entityType.linkText && entityType.linkUrl
                ? { text: entityType.linkText, url: entityType.linkUrl }
                : null,
        searchable: entityType.searchable,
        isMapEntity: entityType.isMapEntity,
        categoryIds,
        customOrderIds: []
    };
}

export function mapGuideToEventView(
    guide: GuideQuery_guide | GuideQueryView_guideView
): EventViewData {
    const getMobileMenuItems = () => {
        const arr = ['home'];

        if (guide.entitiesEnabled && guide.entityTypes.length) {
            arr.push(guide.entityTypes[0].guideEntityTypeId);
        }
        if (guide.worldMapEnabled) {
            arr.push('map');
        }
        if (guide.programEnabled) {
            arr.push('program');
        }
        if (guide.infoEnabled) {
            arr.push('information');
        }
        return arr;
    };

    const getPcMenuItems = () => {
        const arr = ['home'];

        if (guide.entitiesEnabled) {
            arr.push(...guide.entityTypes.map((e) => e.guideEntityTypeId));
        }
        if (guide.programEnabled) {
            arr.push('program');
        }
        if (guide.infoEnabled) {
            arr.push('information');
        }
        if (guide.worldMapEnabled) {
            arr.push('map');
        }
        return arr;
    };

    const getOverviewMenuItems = () => {
        const arr: string[] = [];

        if (guide.entitiesEnabled) {
            arr.push(...guide.entityTypes.map((e) => e.guideEntityTypeId));
        }
        if (guide.programEnabled) {
            arr.push('program');
        }
        if (guide.infoEnabled) {
            arr.push('information');
        }

        if (arr.length <= 2) {
            return { primes: arr, normal: [] };
        }
        return { primes: [arr[0]], normal: arr.slice(1) };
    };

    const { primes, normal } = getOverviewMenuItems();

    const theme = GUIDE_THEMES[guide.visualStyleId] ?? GUIDE_THEMES.default;

    const event: EventViewData = {
        eventId: guide.guideId,
        mapId: guide.worldMapId,
        eventInfo: {
            name: guide.title,
            description: guide.welcomeMessage,
            language: guide.language ?? 'en',
            order: {
                mobile: getMobileMenuItems(),
                wide: getPcMenuItems(),
                primes,
                normal
            },
            externalLinkPosition: 'bottom',
            externalLinkStyle: 'large',
            endDateTime: guide.dateTimeEnd,
            startDateTime: guide.dateTimeStart,
            mapPreviewUrl: guide.worldMapPreviewUrl,
            otherEntities: guide.entityTypes.map((entity) =>
                mapOtherEntityToEventView(guide, entity)
            ),
            program: {
                dateExtensionHours: guide.programExtensionHours,
                items: guide.programItems.map((programItem) => {
                    const today = dayjs().format('YYYY-MM-DD');

                    const startDateTime = dayjs.utc(
                        (programItem.date || today) + ' ' + (programItem.timeStart || '00:00'),
                        'YYYY-MM-DD HH:mm'
                    );
                    let endDateTime = programItem.timeEnd
                        ? dayjs.utc(
                              (programItem.date || today) + ' ' + (programItem.timeEnd || '00:00'),
                              'YYYY-MM-DD HH:mm'
                          )
                        : null;
                    if (endDateTime?.isBefore(startDateTime)) {
                        endDateTime = endDateTime.add(1, 'day');
                    }

                    const firstStory = guide.stories.find(
                        (e) => e.storyId === programItem.storyIds[0]
                    );

                    return {
                        programId: programItem.guideProgramItemId,
                        title: programItem.loadDetailFromStoryId
                            ? firstStory?.header?.title
                            : programItem.title,
                        description: programItem.loadDetailFromStoryId
                            ? firstStory?.header?.text
                            : programItem.description,
                        startDateTime: startDateTime?.toISOString(),
                        endDateTime: endDateTime?.toISOString(),
                        timeStart: programItem.timeStart,
                        timeEnd: programItem.timeEnd,
                        date: programItem.date,
                        storyIds: programItem.storyIds,
                        placeName: programItem.room?.roomName,
                        categoryIds: programItem.categories.map((e) => e.worldMapSetId),
                        markerId: programItem.room?.worldMapMarkerId,
                        autoStoryId: programItem.loadDetailFromStoryId
                    };
                })
            },
            information: {
                listIds: guide.informationItems.map((e) => e.storyId)
            },
            hasMap: guide.worldMapEnabled,
            brandImageUrl: theme.brandImageUrl,
            headerImageUrl: theme.headerImageUrl,
            homepageTopImageUrl: theme.homepageTopImageUrl,
            logoUrl: guide.logoUrl,
            styling: theme.styling,
            externalLinks: guide.externalLinks?.map((e) => ({ title: e.title, url: e.url }))
        }
    };

    return event;
}
